import React, { useState } from 'react';
import { Box, Button, TextField, Slider, Typography, Select, MenuItem, FormControl, InputLabel, Grid } from '@mui/material';
import { TextFieldReadOnly } from '../text-field-readonly/TextFieldReadOnly';
import ReactMarkdown from 'react-markdown';

interface ITextCompletionProps {
    apiKey: string;
}

const models = ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo', 'gpt-3.5-turbo-instruct'];
const responseFormats = ['text', 'json_object', 'json_schema'];

const TextCompletion: React.FC<ITextCompletionProps> = ({ apiKey }) => {
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('No response available.');
    const [model, setModel] = useState(models[0]);
    const [temperature, setTemperature] = useState(1);
    const [maxTokens, setMaxTokens] = useState(2048);
    const [topP, setTopP] = useState(1);
    const [frequencyPenalty, setFrequencyPenalty] = useState(0);
    const [presencePenalty, setPresencePenalty] = useState(0);
    const [responseFormat, setResponseFormat] = useState(responseFormats[0]);
    const [isLoading, setIsLoading] = useState(false);

    const handleGenerate = async () => {
        setIsLoading(true);
        setResponse('');

        try {
            const response = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model,
                    messages: [
                        { role: 'system', content: 'Você é um assistente que ajuda a responder perguntas.' },
                        { role: 'user', content: prompt }
                    ],
                    max_tokens: maxTokens,
                    temperature,
                    top_p: topP,
                    frequency_penalty: frequencyPenalty,
                    presence_penalty: presencePenalty,
                    response_format: { type: responseFormat },
                    stream: true,
                }),
            });

            if (!response.body) throw new Error('Falha na resposta da API');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let done = false;
            let accumulatedResponse = '';

            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;

                const chunk = decoder.decode(value);

                if (chunk.trim() !== '') {
                    const data = chunk
                        .split('\n')
                        .filter(line => line.trim() !== '')
                        .map(line => line.replace(/^data: /, '').trim());

                    data.forEach((chunkData) => {
                        try {
                            const parsed = JSON.parse(chunkData);
                            if (parsed.choices && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                                accumulatedResponse += parsed.choices[0].delta.content;
                                setResponse(accumulatedResponse); // Atualiza a resposta no estado
                            }
                        } catch (err) {
                            console.error('Erro ao parsear o chunk:', err);
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao chamar a API da OpenAI:', error);
            setResponse('Erro ao gerar o texto.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Typography variant="h5" sx={{ mb: 2 }}>Chat Playground</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                    <Grid item xs={12} md={12}>
                        <TextField
                            label="Escreva seu prompt"
                            multiline
                            rows={4}
                            variant="outlined"
                            fullWidth
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerate}
                            disabled={isLoading}
                            sx={{ mt: 2 }}
                        >
                            {isLoading ? 'Gerando...' : 'Submit'}
                        </Button>
                    </Grid>
                    {response && (
                        <Grid item xs={12} md={12}>
                            <ReactMarkdown>{response}</ReactMarkdown>
                        </Grid>
                    )}
                </Grid>
                <Grid item xs={12} md={2}>
                    <Grid item xs={12} md={12}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Select Model</InputLabel>
                            <Select size='small' value={model} onChange={(e) => setModel(e.target.value)}>
                                {models.map((modelOption, index) => (
                                    <MenuItem key={index} value={modelOption}>{modelOption}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Formato da Resposta</InputLabel>
                            <Select size='small' value={responseFormat} onChange={(e) => setResponseFormat(e.target.value)}>
                                {responseFormats.map((format, index) => (
                                    <MenuItem key={index} value={format}>{format}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom>Temperature: {temperature}</Typography>
                        <Slider
                            value={temperature}
                            min={0}
                            max={1}
                            step={0.1}
                            onChange={(e, newValue) => setTemperature(Array.isArray(newValue) ? newValue[0] : newValue)}
                            sx={{ mb: 1 }}
                            size='small'
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom>Max Tokens: {maxTokens}</Typography>
                        <Slider
                            value={maxTokens}
                            min={1}
                            max={4000}
                            step={50}
                            onChange={(e, newValue) => setMaxTokens(Array.isArray(newValue) ? newValue[0] : newValue)}
                            sx={{ mb: 1 }}
                            size='small'
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom>Top P: {topP}</Typography>
                        <Slider
                            value={topP}
                            min={0}
                            max={1}
                            step={0.1}
                            onChange={(e, newValue) => setTopP(Array.isArray(newValue) ? newValue[0] : newValue)}
                            sx={{ mb: 1 }}
                            size='small'
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom>Frequency Penalty: {frequencyPenalty}</Typography>
                        <Slider
                            value={frequencyPenalty}
                            min={0}
                            max={2}
                            step={0.1}
                            onChange={(e, newValue) => setFrequencyPenalty(Array.isArray(newValue) ? newValue[0] : newValue)}
                            sx={{ mb: 1 }}
                            size='small'
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom>Presence Penalty: {presencePenalty}</Typography>
                        <Slider
                            value={presencePenalty}
                            min={0}
                            max={2}
                            step={0.1}
                            onChange={(e, newValue) => setPresencePenalty(Array.isArray(newValue) ? newValue[0] : newValue)}
                            sx={{ mb: 1 }}
                            size='small'
                        />
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default TextCompletion;
